// PURPOSE: The one Outreach row shape every tab uses: a white card per lead, one action, the rest behind ⋯.
// CONTRACT: Clicking the row opens its details in place; the name links to the full record; red only for late.
// DEPENDS: Router links and shared outreach styles.
// CEILING: Presentation only; callers own every action and permission check.
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Star } from 'lucide-react';
import type { NativeRecord } from './types';

export const leadName = (record: NativeRecord) => String(record.data.name || 'Unnamed company');
export const leadPath = (id: string) => `/records/client/${encodeURIComponent(id)}`;
export const leadScore = (record: NativeRecord) => Number(record.posting?.score ?? record.data.score ?? 0);
export function leadSub(record: NativeRecord, ...extra: (string | false | undefined | null)[]) {
  return [record.posting?.role_title || String(record.data.role_title || ''), record.posting?.location || String(record.data.location || ''), ...extra].filter(Boolean).join(' · ');
}

/** "3 days overdue" when an ISO date is in the past, otherwise "" so callers keep their own wording. */
export function overdueLabel(due: unknown) {
  const value = String(due || ''); if (!value) return '';
  const date = new Date(value.length === 10 ? `${value}T23:59:59` : value);
  const days = Math.floor((Date.now() - date.getTime()) / 86_400_000);
  return Number.isFinite(days) && days >= 1 ? `${days} ${days === 1 ? 'day' : 'days'} overdue` : '';
}

export function LeadCard({ id, name, to, sub, starred, score, status, late, action, menu, select, open, onToggle, busy, children, after }: {
  id: string; name: string; to?: string; sub?: string; starred?: boolean; score?: number; status?: ReactNode; late?: boolean;
  action?: ReactNode; menu?: ReactNode; select?: ReactNode; open?: boolean; onToggle?(): void; busy?: boolean; children?: ReactNode; after?: ReactNode;
}) {
  const detailsId = `lead-details-${id}`;
  return <article data-row-id={id} aria-busy={busy} className={`lead-card${open ? ' is-open' : ''}`} aria-label={name}>
    <div className="lead-line" onClick={event => { if ((event.target as HTMLElement).closest('a, button, input, label, select, textarea, [role=menu]')) return; if (onToggle) onToggle(); else event.currentTarget.querySelector<HTMLAnchorElement>('.lead-name a')?.click(); }}>
      {select ?? <span className="lead-select" aria-hidden="true" />}
      <div className="lead-who">
        <p className="lead-name">{to ? <Link to={to}>{name}</Link> : name}{starred && <Star size={14} fill="currentColor" role="img" aria-label="Starred" />}</p>
        {sub && <p className="lead-sub">{sub}</p>}
        {status && <p className={`lead-status-phone${late ? ' late' : ''}`}>{status}</p>}
      </div>
      {onToggle && <button type="button" data-row-expand className="lead-expand" aria-label={`${open ? 'Hide' : 'Show'} details for ${name}`} aria-expanded={!!open} aria-controls={detailsId} onClick={onToggle} />}
      {score !== undefined && <span className={`lead-score${score >= 11 ? ' high' : score >= 8 ? ' medium' : ''}`}>{score} / 15</span>}
      <span className={`lead-status${late ? ' late' : ''}`}>{status}</span>
      <span className="lead-act">{action}</span>
      <span className="lead-more">{menu}</span>
    </div>
    {open && <div id={detailsId} className="lead-body">{children}</div>}
    {after}
  </article>;
}

export function LeadEmpty({ text, team, onShowTeam }: { text: string; team?: number; onShowTeam?(): void }) {
  return <p className="lead-empty">{text}{!!team && onShowTeam && <> <button type="button" className="text-button" onClick={onShowTeam}>Show the team's {team}</button></>}</p>;
}
